/**
 * Browser and OS detection utility for DuoCast.
 * Used by PermissionDenied to show device-specific instructions.
 */

export type BrowserName = 'chrome' | 'safari' | 'firefox' | 'edge' | 'samsung' | 'unknown';
export type OSName = 'ios' | 'android' | 'macos' | 'windows' | 'linux' | 'unknown';

export interface BrowserInfo {
  browser: BrowserName;
  os: OSName;
  version: string;
}

function detectOS(ua: string): OSName {
  // iOS detection — must come before macOS since iPad can report Mac UA
  if (/iPad|iPhone|iPod/.test(ua)) {
    return 'ios';
  }
  // iPad OS 13+ reports as Mac in UA but has touch
  if (/Macintosh/.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1) {
    return 'ios';
  }
  if (/Android/.test(ua)) {
    return 'android';
  }
  if (/Mac\s?OS|Macintosh/.test(ua)) {
    return 'macos';
  }
  if (/Windows/.test(ua)) {
    return 'windows';
  }
  if (/Linux/.test(ua)) {
    return 'linux';
  }
  return 'unknown';
}

function detectBrowser(ua: string): { browser: BrowserName; version: string } {
  // Order matters: more specific checks first

  // Samsung Internet
  const samsungMatch = ua.match(/SamsungBrowser\/(\d+[\d.]*)/);
  if (samsungMatch) {
    return { browser: 'samsung', version: samsungMatch[1] };
  }

  // Edge (Chromium-based)
  const edgeMatch = ua.match(/Edg(?:e|A|iOS)?\/(\d+[\d.]*)/);
  if (edgeMatch) {
    return { browser: 'edge', version: edgeMatch[1] };
  }

  // Firefox
  const firefoxMatch = ua.match(/Firefox\/(\d+[\d.]*)/);
  if (firefoxMatch) {
    return { browser: 'firefox', version: firefoxMatch[1] };
  }

  // Chrome (must be after Edge and Samsung since they include Chrome in UA)
  const chromeMatch = ua.match(/(?:Chrome|CriOS)\/(\d+[\d.]*)/);
  if (chromeMatch) {
    return { browser: 'chrome', version: chromeMatch[1] };
  }

  // Safari (must be last since many browsers include Safari in UA)
  const safariMatch = ua.match(/Version\/(\d+[\d.]*).*Safari/);
  if (safariMatch) {
    return { browser: 'safari', version: safariMatch[1] };
  }

  return { browser: 'unknown', version: '' };
}

/**
 * Detect browser, OS, and version from the user agent string.
 * Falls back to navigator.userAgentData when available for more reliable detection.
 */
export function getBrowserInfo(): BrowserInfo {
  if (typeof navigator === 'undefined') {
    return { browser: 'unknown', os: 'unknown', version: '' };
  }

  const ua = navigator.userAgent;

  // Try navigator.userAgentData for Chromium-based browsers (more reliable)
  const uaData = (navigator as any).userAgentData;
  if (uaData) {
    const brands: Array<{ brand: string; version: string }> = uaData.brands ?? [];
    const platform: string = uaData.platform ?? '';

    let browser: BrowserName = 'unknown';
    let version = '';

    for (const entry of brands) {
      const name = entry.brand.toLowerCase();
      if (name.includes('samsung')) {
        browser = 'samsung';
        version = entry.version;
        break;
      }
      if (name.includes('edge') || name.includes('edg')) {
        browser = 'edge';
        version = entry.version;
        break;
      }
      if (name.includes('chrome') || name.includes('chromium')) {
        browser = 'chrome';
        version = entry.version;
        // Don't break — a more specific brand may follow
      }
    }

    let os: OSName = 'unknown';
    const p = platform.toLowerCase();
    if (p === 'android') os = 'android';
    else if (p === 'ios') os = 'ios';
    else if (p === 'macos' || p === 'mac os x') os = 'macos';
    else if (p === 'windows') os = 'windows';
    else if (p === 'linux') os = 'linux';

    if (browser !== 'unknown') {
      return { browser, os: os !== 'unknown' ? os : detectOS(ua), version };
    }
  }

  // Fallback to classic UA parsing
  const os = detectOS(ua);
  const { browser, version } = detectBrowser(ua);

  return { browser, os, version };
}
